import { useState, useContext, useEffect, useMemo, useRef } from 'react'
import TextField from '@material-ui/core/TextField'
import { Chip, Popper, PopperPlacementType, Typography, useTheme } from '@material-ui/core'
import useMuseStyles from '../../themes/useMuseStyles'
import { UserCommunityProfileView, UserContext, UserContextType } from '../../UserContext'
import { useCommunityFeature } from '../../services/useCommunityFeature'
import { Autocomplete } from '@material-ui/lab'
import useDebounce from '../../hooks/useDebounce'
import { makeStyles } from '@material-ui/core'
import { AppContext, AppContextType } from '../../AppContext'

const useStyles = makeStyles((theme) => ({
    autocomplete: {
        height: '100%',

        '& .MuiFormControl-root': {
            height: '100%',
        },
        '& .MuiInputBase-root': {
            padding: 0,
            paddingLeft: '36px',
            color: '#f1f1f1',
            fontSize: '98%',
            height: '100%',
            [theme.breakpoints.down('xs')]: {
                fontSize: '16px',
            },
        },
        '& .MuiChip-root': {
            borderColor: '#f9cc04',
            color: '#f9cc04',
            height: '26px',
        },
        '& .MuiChip-deleteIcon': {
            color: '#f9cc04',
            width: '16px',
        },
        '& .MuiFilledInput-input': {
            padding: '0 8px',
        },
        '& .MuiFilledInput-underline:before': {
            borderBottomColor: '#3F3F3F',
        },
        '& .MuiAutocomplete-clearIndicator': {
            color: '#f9cc04',
        },
        '& .MuiAutocomplete-popupIndicator': {
            display: 'none',
        },
    },

    autocompletePaper: {
        margin: '4px 32px',
        background: '#4f4f4f',

        '& .MuiAutocomplete-noOptions': {
            display: 'none',
            // background: '#4f4f4f',
            // color: '#f1f1f1',
            // fontSize: '14px',
            // padding: '9px 16px',
        },
    },

    autocompleteListbox: {
        // border: '1px solid #F9CC04',
        padding: '3px 0',
        background: '#4f4f4f',
        color: '#f1f1f1',
        fontSize: '14px',
    },
}))

type UserCommunityProfileViewPartial = {
    userUrn: string
    username: string
    name: string
}

export default function UserSelectorAutoComplete({
    communityFeature,
    userUrns,
    onSelectionChanged,
    popperPlacement = 'bottom-start',
}: {
    communityFeature: ReturnType<typeof useCommunityFeature>
    userUrns: string[]
    onSelectionChanged: (userUrns: string[]) => void
    popperPlacement?: PopperPlacementType
}) {
    const { state } = useContext(UserContext) as UserContextType
    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const gclasses = useMuseStyles(useTheme())
    const styles = useStyles(useTheme())

    const [options, set_options] = useState<UserCommunityProfileViewPartial[]>([])
    const userUrnLookupRef = useRef<Map<string, string>>(new Map())
    const [searchString, set_searchString] = useState('')
    const searchString_debounce = useDebounce(searchString, 250)

    const options_default = useMemo(() => {
        return (state.community.collaborators || []).map((u) => ({
            userUrn: u.userUrn,
            username: u.username,
            name: u.name,
        }))
    }, [])

    useEffect(() => {
        options_default.map((u) => {
            userUrnLookupRef.current.set(u.username, u.userUrn)
        })
    }, [options_default])

    useEffect(() => {
        if (searchString_debounce) {
            communityFeature.searchUser({ username: searchString_debounce })
        } else {
            set_options(options_default)
        }
    }, [searchString_debounce])

    useEffect(() => {
        if ((searchString_debounce || '').trim().length === 0) {
            return
        }

        const users: UserCommunityProfileView[] = communityFeature.searchUsersResult || []

        const users_filtered = users.filter((u) => {
            return !userUrns.includes(u.userUrn)
        })
        set_options(
            users_filtered.map((u) => {
                userUrnLookupRef.current.set(u.username, u.userUrn)
                return {
                    userUrn: u.userUrn,
                    username: u.username,
                    name: u.name,
                }
            })
        )
    }, [communityFeature.searchUsersResult])

    return (
        <div style={{ height: '100%', position: 'relative' }}>
            <div
                style={{
                    position: 'absolute',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: '10px',
                }}
            >
                <Typography variant="body1">To:</Typography>
            </div>
            <Autocomplete
                style={{ width: '100%' }}
                className={styles.autocomplete}
                classes={{
                    listbox: styles.autocompleteListbox,
                    paper: styles.autocompletePaper,
                }}
                multiple
                limitTags={5}
                options={options.map((option) => option.username)}
                defaultValue={[]}
                // freeSolo
                onInputChange={(e) => {
                    // @ts-ignore
                    const value = e?.target?.value || null
                    set_searchString(value || '')
                }}
                onChange={(event, value) => {
                    // console.log('value=', JSON.stringify(value))
                    // console.log('userUrnLookupRef=%O', userUrnLookupRef)
                    const userUrns = value
                        .map((username) => {
                            return userUrnLookupRef.current.get(username) || null
                        })
                        .filter((v) => v != null) as string[]

                    // appDispatch({
                    //     type: 'root_snackbar_history_append',
                    //     value: {
                    //         severity: 'error',
                    //         message: `The user ${unresolvedUsername} does not exist!`,
                    //     }
                    // })
                    onSelectionChanged(userUrns)
                }}
                renderTags={(value, getTagProps) => {
                    return value.map((option, index) => (
                        <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                    ))
                }}
                renderInput={(params) => {
                    return (
                        <TextField
                            {...params}
                            variant="filled"
                            placeholder="Add user by their username"
                            // this causes autosuggest to not work.
                            //InputProps={{
                            //    startAdornment: 'To: '
                            //}}
                        />
                    )
                }}
                PopperComponent={(props) => {
                    // Makes the suggestion list appear inside the modal and not the parent frame, if the Autocomplete is in a modal.
                    // <https://stackoverflow.com/questions/60951341/incorrect-dropdown-positioning-of-autocomplete-component-for-react-material-ui>

                    const { className, anchorEl, style, ...rest } = props
                    // @ts-ignore
                    const bound = anchorEl?.getBoundingClientRect() || {}

                    //return <div
                    //    {...rest}
                    //    style={{/*position:'absolute',*/ zIndex:1000, width:bound.width}}
                    ///>
                    return (
                        <Popper
                            {...props}
                            style={{ zIndex: 1000, width: bound.width }}
                            placement={popperPlacement}
                            disablePortal={true}
                        />
                    )
                }}
            />
        </div>
    )
}

export const useStyles_UserSelectorAutoComplete = useStyles
